import {Component, Input, OnInit} from '@angular/core';
import {CommonModule} from '@angular/common';
import {FormsModule} from '@angular/forms';
import {Rule, RuleCondition, RuleGroup, RuleOperator} from '../../../shared/models/form-schema.model';
import {BuilderStateService, FieldKeyRef} from '../../services/builder-state.service';
import {SelectComponent} from '../../../../../_ui/components/controls/select/select.component';

interface OperatorOption {
  value: RuleOperator;
  label: string;
}

/** One entry of the "Treffer wenn" picker. */
interface ConditionOption {
  value: RuleCondition;
  label: string;
}

/**
 * One entry of the field picker.
 *
 * `label` leads with the field's human label and carries its data key in parentheses behind it. The order is
 * the point: it is also what the dropdown searches on, so an author who types "Geburts" finds
 * "Geburtsdatum (person.geburtsdatum)". A native `<select>`'s browser type-ahead matches the option text from
 * its first characters instead, which is how technical keys end up ahead of the names an author thinks in.
 */
interface FieldPickerChoice {
  path: string;
  label: string;
}

/** One entry of a value picker — either the fixed Ja/Nein pair, or the referenced field's own options. */
interface ValueChoice {
  label: string;
  value: any;
}

const OPERATORS: OperatorOption[] = [
  {value: 'equals', label: 'ist gleich'},
  {value: 'notEquals', label: 'ist nicht gleich'},
  {value: 'contains', label: 'enthält'},
  {value: 'startsWith', label: 'beginnt mit'},
  {value: 'greaterThan', label: 'ist größer als'},
  {value: 'greaterThanOrEqual', label: 'ist größer/gleich'},
  {value: 'lessThan', label: 'ist kleiner als'},
  {value: 'lessThanOrEqual', label: 'ist kleiner/gleich'},
  {value: 'isEmpty', label: 'ist leer'},
  {value: 'isNotEmpty', label: 'ist nicht leer'}
];

const CONDITIONS: ConditionOption[] = [
  {value: 'AND', label: 'ALLE (UND)'},
  {value: 'OR', label: 'EINE (ODER)'}
];

const BOOLEAN_CHOICES: ValueChoice[] = [
  {value: true, label: 'Ja'},
  {value: false, label: 'Nein'}
];

/**
 * The empty option list, as ONE array.
 *
 * `<cqme-select>` hands `[options]` straight to ng-select, which rebuilds its item list whenever that array's
 * identity changes — and a template getter returning a fresh `[]` would hand it a new one on every
 * change-detection pass.
 */
const NO_VALUE_CHOICES: ValueChoice[] = [];

@Component({
  selector: 'forms-rule-editor',
  standalone: true,
  imports: [CommonModule, FormsModule, SelectComponent],
  template: `
    <div class="rule-editor p-3 sm:p-4 bg-gray-50/50 rounded-2xl border border-gray-100 shadow-inner">
      <div class="flex flex-wrap items-center gap-x-3 gap-y-2 mb-4">
        <span class="text-[10px] font-black text-gray-500 uppercase tracking-[0.2em]">Treffer wenn</span>
        <!-- Every dropdown in this app is the shared <cqme-select>; a native <select> is not allowed, because
             its browser type-ahead reads the raw option text and it ignores the theme tokens. -->
        <cqme-select data-testid="rule-group-condition" class="w-40"
                     [options]="conditions" [labelKey]="'label'" [valueKey]="'value'"
                     size="sm" [clearable]="false" placeholder="Bedingung" ariaLabel="Verknüpfung der Bedingungen"
                     [current]="ruleGroup.condition"
                     (currentChange)="setCondition($event)"></cqme-select>
        <span class="text-[10px] font-black text-gray-500 uppercase tracking-[0.2em]">Bedingungen zutreffen:</span>
      </div>

      <div class="space-y-3">
        @for (item of ruleGroup.rules; track $index) {
          @if (isGroup(item)) {
            <!-- Nested group -->
            <div class="relative">
              <button type="button" (click)="removeItem($index)" aria-label="Gruppe entfernen"
                      class="absolute -top-2 -right-2 z-10 p-1 bg-white text-gray-300 hover:text-red-500 rounded-full shadow border border-gray-100 transition-all">
                <svg xmlns="http://www.w3.org/2000/svg" class="h-3.5 w-3.5" viewBox="0 0 20 20" fill="currentColor">
                  <path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd"/>
                </svg>
              </button>
              <forms-rule-editor [ruleGroup]="asGroup(item)" [excludeBlockId]="excludeBlockId"></forms-rule-editor>
            </div>
          } @else {
            <!-- Single rule -->
            <div class="p-3 bg-white rounded-2xl border border-gray-100 shadow-sm space-y-2.5 relative group animate-in fade-in slide-in-from-top-2 duration-200">
              <div class="flex items-center justify-between">
                <!-- -700, not -400: the theme is Tailwind's pink, and pink-400 on white measures 2.64:1 at
                     10px — well under the 4.5:1 floor. Caught by the axe check in this component's spec. -->
                <span class="text-[10px] font-bold text-theme-primary-700 uppercase tracking-widest">Regel #{{ $index + 1 }}</span>
                <button type="button" (click)="removeItem($index)" aria-label="Regel entfernen"
                        class="p-1.5 text-gray-300 hover:text-red-500 hover:bg-red-50 rounded-lg transition-all opacity-0 group-hover:opacity-100">
                  <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                    <path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd"/>
                  </svg>
                </button>
              </div>

              <!-- Field selector. Human label first, data key behind it — and it is searched on that text, so
                   an author finds a field by its name (see FieldPickerChoice). -->
              <cqme-select data-testid="rule-field" class="block w-full"
                           [options]="fieldChoicesFor(asRule(item))" [labelKey]="'label'" [valueKey]="'path'"
                           size="md" [clearable]="false" placeholder="– Feld wählen –" ariaLabel="Feld"
                           [current]="pickedFieldKey(asRule(item))"
                           (currentChange)="setFieldKey(asRule(item), $event)"></cqme-select>

              <div class="flex flex-wrap gap-2">
                <cqme-select data-testid="rule-operator" class="flex-grow min-w-[8rem]"
                             [options]="operators" [labelKey]="'label'" [valueKey]="'value'"
                             size="md" [clearable]="false" placeholder="– Operator wählen –" ariaLabel="Operator"
                             [current]="asRule(item).operator"
                             (currentChange)="setOperator(asRule(item), $event)"></cqme-select>

                @if (needsValue(asRule(item))) {
                  @switch (valueInputType(asRule(item))) {
                    @case ('boolean') {
                      <cqme-select data-testid="rule-value-boolean" class="w-28 shrink-0"
                                   [options]="booleanChoices" [labelKey]="'label'" [valueKey]="'value'"
                                   size="md" [clearable]="false" placeholder="Ja / Nein" ariaLabel="Wert"
                                   [current]="pickedValue(asRule(item), booleanChoices)"
                                   (currentChange)="setValue(asRule(item), $event)"></cqme-select>
                    }
                    @case ('options') {
                      <cqme-select data-testid="rule-value-options" class="flex-grow min-w-[6rem]"
                                   [options]="valueChoicesFor(asRule(item))" [labelKey]="'label'" [valueKey]="'value'"
                                   size="md" [clearable]="false" placeholder="– Wert wählen –" ariaLabel="Wert"
                                   [current]="pickedValue(asRule(item), valueChoicesFor(asRule(item)))"
                                   (currentChange)="setValue(asRule(item), $event)"></cqme-select>
                    }
                    @case ('number') {
                      <input type="number" [(ngModel)]="asRule(item).value" placeholder="Wert"
                             class="text-[11px] rounded-xl border-gray-100 bg-gray-50 w-28 py-2.5 px-4 font-medium text-gray-900 focus:bg-white focus:ring-4 focus:ring-theme-primary-50 transition-all outline-none border shadow-sm"/>
                    }
                    @case ('date') {
                      <input type="date" [(ngModel)]="asRule(item).value"
                             class="text-[11px] rounded-xl border-gray-100 bg-gray-50 flex-grow min-w-[8rem] py-2.5 px-3 font-medium text-gray-900 focus:bg-white focus:ring-4 focus:ring-theme-primary-50 transition-all outline-none border shadow-sm"/>
                    }
                    @case ('time') {
                      <input type="time" [(ngModel)]="asRule(item).value"
                             class="text-[11px] rounded-xl border-gray-100 bg-gray-50 flex-grow min-w-[8rem] py-2.5 px-3 font-medium text-gray-900 focus:bg-white focus:ring-4 focus:ring-theme-primary-50 transition-all outline-none border shadow-sm"/>
                    }
                    @default {
                      <input [(ngModel)]="asRule(item).value" placeholder="Wert"
                             class="text-[11px] rounded-xl border-gray-100 bg-gray-50 flex-grow min-w-[6rem] py-2.5 px-4 font-medium text-gray-900 focus:bg-white focus:ring-4 focus:ring-theme-primary-50 transition-all outline-none border shadow-sm"/>
                    }
                  }
                }
              </div>
            </div>
          }
        }
      </div>

      <div class="mt-4 flex flex-wrap gap-2">
        <button (click)="addRule()"
                class="flex-grow py-2.5 border-2 border-dashed border-gray-200 rounded-2xl text-[10px] font-black text-gray-500 hover:text-theme-primary-600 hover:border-theme-primary-200 hover:bg-theme-primary-50/30 transition-all uppercase tracking-[0.2em] active:scale-95">
          + Regel hinzufügen
        </button>
        <button (click)="addGroup()"
                class="py-2.5 px-4 border-2 border-dashed border-gray-200 rounded-2xl text-[10px] font-black text-gray-500 hover:text-theme-primary-600 hover:border-theme-primary-200 hover:bg-theme-primary-50/30 transition-all uppercase tracking-[0.2em] active:scale-95">
          + Gruppe
        </button>
      </div>
    </div>
  `
})
export class RuleEditorComponent implements OnInit {
  @Input() ruleGroup!: RuleGroup;
  /** Block currently being edited — excluded from the field list to prevent self-reference. */
  @Input() excludeBlockId?: string;

  fields: FieldKeyRef[] = [];
  readonly operators = OPERATORS;
  readonly conditions = CONDITIONS;
  readonly booleanChoices = BOOLEAN_CHOICES;

  /**
   * Every selectable field, as the picker shows it.
   *
   * Built once in {@link ngOnInit} rather than mapped per change-detection pass, because `<cqme-select>` hands
   * `[options]` straight to ng-select, which rebuilds its item list whenever the array's identity changes — a
   * fresh array every pass would do that *while the author has the dropdown open*. The set cannot move
   * underneath the editor anyway: it is snapshotted from the definition when the rule panel opens.
   */
  fieldChoices: FieldPickerChoice[] = [];

  /** Value choices per field path, for option-backed fields. Built alongside {@link fieldChoices}. */
  private valueChoices = new Map<string, ValueChoice[]>();

  /**
   * Field lists that also carry a hand-written key naming no field in the definition, cached per key.
   *
   * A stored rule may point at a key the definition no longer has (the field was renamed or deleted). Dropping
   * it silently would hide the fact that the author has to fix it, so it stays on offer, marked "(manuell)" —
   * and the array it lives in has to keep a stable identity like every other option list.
   */
  private manualFieldChoices = new Map<string, FieldPickerChoice[]>();

  constructor(private state: BuilderStateService) {
  }

  ngOnInit() {
    this.fields = this.state.availableFieldKeys(this.excludeBlockId);
    this.fieldChoices = this.fields.map(f => ({
      path: f.path,
      label: `${f.withinRow ? '↳ ' : ''}${f.label} (${f.path})${f.withinRow ? ' · diese Zeile' : ''}`
    }));
    for (const field of this.fields) {
      if (field.options?.length) {
        this.valueChoices.set(field.path, field.options.map(o => ({
          label: o.label || String(o.value ?? ''),
          value: o.value
        })));
      }
    }
  }

  /** The fields one rule may point at — everything on offer, plus its own hand-written key if it has one. */
  fieldChoicesFor(rule: Rule): FieldPickerChoice[] {
    const key = rule.fieldKey;
    if (!key || this.knownField(key)) return this.fieldChoices;
    let withManual = this.manualFieldChoices.get(key);
    if (!withManual) {
      withManual = [...this.fieldChoices, {path: key, label: `${key} (manuell)`}];
      this.manualFieldChoices.set(key, withManual);
    }
    return withManual;
  }

  /** The value choices a rule's referenced field offers, or none — always the same array for the same field. */
  valueChoicesFor(rule: Rule): ValueChoice[] {
    return this.valueChoices.get(rule.fieldKey) ?? NO_VALUE_CHOICES;
  }

  /**
   * What the field picker should show, or `undefined` for its placeholder.
   *
   * A fresh rule starts at `''`, which ng-select treats as a *value*: it would select a nameless item and
   * swallow the "– Feld wählen –" placeholder.
   */
  pickedFieldKey(rule: Rule): string | undefined {
    return rule.fieldKey || undefined;
  }

  /**
   * What a value picker should show, or `undefined` for its placeholder — same reason as
   * {@link pickedFieldKey}: a rule's `value` starts as `''`, which names none of the offered options.
   */
  pickedValue(rule: Rule, choices: ValueChoice[]): any {
    return choices.some(choice => choice.value === rule.value) ? rule.value : undefined;
  }

  /**
   * Store how this group's conditions combine.
   *
   * Every one of these setters takes a wider parameter than it stores, because `<cqme-select>`'s `currentChange`
   * is typed for the multi-select case as well. Neither an array nor a cleared value can arrive from a single,
   * non-clearable picker, and both are ignored rather than written as a broken rule.
   */
  setCondition(condition: RuleCondition | RuleCondition[] | undefined) {
    if (!condition || Array.isArray(condition)) return;
    this.ruleGroup.condition = condition;
  }

  /** Point a rule at a field. */
  setFieldKey(rule: Rule, path: string | string[] | undefined) {
    if (Array.isArray(path)) return;
    rule.fieldKey = path ?? '';
  }

  /** Store a rule's comparison. */
  setOperator(rule: Rule, operator: RuleOperator | RuleOperator[] | undefined) {
    if (!operator || Array.isArray(operator)) return;
    rule.operator = operator;
  }

  /** Store the value a rule compares against (booleans and option values are picked, not typed). */
  setValue(rule: Rule, value: any) {
    if (Array.isArray(value)) return;
    rule.value = value;
  }

  isGroup(item: Rule | RuleGroup): boolean {
    return 'condition' in item;
  }

  asRule(item: Rule | RuleGroup): Rule {
    return item as Rule;
  }

  asGroup(item: Rule | RuleGroup): RuleGroup {
    return item as RuleGroup;
  }

  knownField(path: string): boolean {
    return this.fields.some(f => f.path === path);
  }

  addRule() {
    this.ruleGroup.rules.push({fieldKey: this.fields[0]?.path ?? '', operator: 'equals', value: ''});
  }

  addGroup() {
    this.ruleGroup.rules.push({condition: 'AND', rules: []});
  }

  removeItem(index: number) {
    this.ruleGroup.rules.splice(index, 1);
  }

  needsValue(rule: Rule): boolean {
    return rule.operator !== 'isEmpty' && rule.operator !== 'isNotEmpty';
  }

  private refFor(rule: Rule): FieldKeyRef | undefined {
    return this.fields.find(f => f.path === rule.fieldKey);
  }

  valueInputType(rule: Rule): 'boolean' | 'options' | 'number' | 'date' | 'time' | 'text' {
    const ref = this.refFor(rule);
    if (!ref) return 'text';
    switch (ref.type) {
      case 'boolean':
        return 'boolean';
      case 'select':
      case 'radio':
      case 'multiselect':
      case 'checkbox-group':
        return ref.options && ref.options.length ? 'options' : 'text';
      case 'number':
        return 'number';
      case 'date':
        return 'date';
      case 'time':
        return 'time';
      default:
        return 'text';
    }
  }
}
